import React, { useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import styled from 'styled-components'
import Button from '../../shared/Button'
import ScreenWrapper from '../../shared/ScreenWrapper'
import styles from '../../../utility/styles'
import { post } from '../../../utility/api'

var font = styles.fonts.geist
var mono = "'SF Mono', Monaco, 'Cascadia Code', monospace"

// -- Helpers --

function countryFlag(code) {
	if (!code || code.length !== 2) return null
	var offset = 0x1F1E6 - 65
	return String.fromCodePoint(code.charCodeAt(0) + offset, code.charCodeAt(1) + offset)
}

function parseDevice(ua) {
	if (!ua) return null
	if (/PostmanRuntime/i.test(ua)) return 'API Client'
	if (/iPhone/i.test(ua)) return 'iPhone'
	if (/iPad/i.test(ua)) return 'iPad'
	if (/Android/i.test(ua)) return 'Android'
	if (/Macintosh|Mac OS/i.test(ua)) return 'Mac'
	if (/Windows/i.test(ua)) return 'Windows'
	if (/Linux/i.test(ua)) return 'Linux'
	if (/CrOS/i.test(ua)) return 'ChromeOS'
	return null
}

function categoryLabel(session) {
	if (session.category === 'mcp') return session.has_commands ? 'MCP · Read + Commands' : 'MCP · Read Only'
	if (session.category === 'mobile') {
		if (session.app_name === 'TezLab iOS') return 'iOS App'
		if (session.app_name === 'TezLab Android') return 'Android App'
		return 'Mobile App'
	}
	if (session.category === 'watch') return 'Watch'
	return 'Web'
}

function timeAgo(dateStr) {
	var now = new Date()
	var date = new Date(dateStr)
	var seconds = Math.floor((now - date) / 1000)
	if (seconds < 60) return 'just now'
	var minutes = Math.floor(seconds / 60)
	if (minutes < 60) return minutes + 'm ago'
	var hours = Math.floor(minutes / 60)
	if (hours < 24) return hours + 'h ago'
	var days = Math.floor(hours / 24)
	if (days === 1) return 'yesterday'
	if (days < 30) return days + 'd ago'
	var months = Math.floor(days / 30)
	return months + 'mo ago'
}

function isActiveNow(dateStr) {
	return (new Date() - new Date(dateStr)) < 15 * 60 * 1000
}

// -- Styled Components --

var StyledContainer = styled(Container)`
	margin-top: 72px;
	margin-bottom: 72px;
	@media only screen and (max-width: 768px) {
		padding: 0 24px;
	}
`

var BackLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: ${styles.colors.lightGray};
	font-size: 14px;
	font-family: ${font};
	font-weight: 600;
	text-decoration: none;
	transition: color 0.15s ease;
	&:hover, &:focus, &:active {
		color: ${styles.colors.whiteGray};
		text-decoration: none;
	}
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: 2px solid rgba(16, 171, 255, 0.5);
		outline-offset: 3px;
		border-radius: 6px;
	}
`

var BackArrow = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 6px;
	background: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
	color: ${styles.colors.whiteGray};
	font-size: 14px;
	transition: background 0.15s ease;
	${BackLink}:hover & {
		background: ${styles.colors.mediumGray};
	}
`

var PageHeader = styled.div`
	margin-top: 28px;
	margin-bottom: 24px;
`

var Title = styled.h1`
	font-size: 24px;
	font-weight: 600;
	font-family: ${font};
	color: ${styles.colors.dimHighlight};
	margin: 0 0 4px;
`

var Subtitle = styled.p`
	font-size: 14px;
	font-family: ${font};
	font-weight: 600;
	color: ${styles.colors.lightGray};
	margin: 0;
`

var SessionCard = styled.div`
	background: ${styles.colors.darkBackground};
	border-radius: 10px;
	overflow: hidden;
	& + & { margin-top: 6px; }
`

var SessionItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 18px;
	gap: 16px;
	transition: background 0.12s ease;
	&:hover { background: var(--tz-wash, rgba(255, 255, 255, 0.03)); }
	& + & { border-top: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.05)); }
	@media only screen and (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
		padding: 14px;
	}
`

var SessionLeft = styled.div`
	display: flex;
	flex-direction: column;
	gap: 5px;
	flex: 1;
	min-width: 0;
`

var SessionNameRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
`

var SessionName = styled.span`
	font-size: 14px;
	font-weight: 600;
	font-family: ${font};
	color: ${styles.colors.dimHighlight};
`

var TypeLabel = styled.span`
	font-size: 11px;
	font-weight: 600;
	font-family: ${mono};
	letter-spacing: 0.2px;
	color: ${styles.colors.blue};
	background: rgba(16, 171, 255, 0.1);
	padding: 2px 7px;
	border-radius: 4px;
`

var ActiveIndicator = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	font-size: 11px;
	font-family: ${mono};
	font-weight: 600;
	color: ${styles.colors.chargingGreen};
	&::before {
		content: '';
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: ${styles.colors.chargingGreen};
		box-shadow: 0 0 6px ${styles.colors.chargingGreen};
	}
`

var MetaRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0;
	flex-wrap: wrap;
`

var MetaChip = styled.span`
	font-size: 12px;
	font-family: ${mono};
	color: ${styles.colors.lightGray};
`

var MetaDivider = styled.span`
	display: inline-block;
	width: 3px;
	height: 3px;
	border-radius: 50%;
	background: ${styles.colors.mediumGray};
	margin: 0 8px;
	flex-shrink: 0;
`

var SessionRight = styled.div`
	display: flex;
	align-items: center;
	flex-shrink: 0;
	@media only screen and (max-width: 768px) {
		justify-content: flex-end;
	}
`

var EmptyState = styled.div`
	background: ${styles.colors.darkBackground};
	border-radius: 10px;
	padding: 48px 24px;
	text-align: center;
`

export default function ManageSessions({ data: { sessions: initialSessions } }) {
	var [sessions, setSessions] = useState(initialSessions || [])
	var [revokingId, setRevokingId] = useState(null)

	function getMagicLinkKey() {
		return window.location.pathname.split('/')[2]
	}

	async function revokeSession(tokenId) {
		if (!window.confirm('Revoke this session? The app or device will need to sign in again.')) return
		setRevokingId(tokenId)
		var key = getMagicLinkKey()
		var result = await post('/subscribe/' + key + '/manage/revoke_session', { token_id: tokenId })
		if (result.success && result.data?.sessions) {
			setSessions(result.data.sessions)
		}
		setRevokingId(null)
	}

	return (
		<ScreenWrapper>
			<StyledContainer>
				<Row>
					<Col>
						<BackLink href={'/subscribe/' + getMagicLinkKey() + '/manage'}>
							<BackArrow>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
							</BackArrow>
							Account
						</BackLink>

						<PageHeader>
							<Title>Active Sessions</Title>
							{sessions.length > 0 && (
								<Subtitle>
									{sessions.length + ' active session' + (sessions.length === 1 ? '' : 's')}
								</Subtitle>
							)}
						</PageHeader>

						{!sessions.length && (
							<EmptyState>
								<Subtitle>No active sessions found.</Subtitle>
							</EmptyState>
						)}

						<SessionCard>
							{sessions.map(function (session) {
								var displayName = session.client_name || session.app_name
								var device = parseDevice(session.user_agent)
								var flag = countryFlag(session.country_code)
								var city = session.city
								var active = isActiveNow(session.created_at)

								var meta = []
								if (active) {
									meta.push({ type: 'active' })
								} else {
									meta.push({ type: 'text', value: timeAgo(session.created_at) })
								}
								if (device) meta.push({ type: 'text', value: device })
								if (city && flag) {
									meta.push({ type: 'text', value: flag + '\u2002' + city })
								} else if (city) {
									meta.push({ type: 'text', value: city })
								} else if (flag) {
									meta.push({ type: 'text', value: flag })
								}

								return (
									<SessionItem key={session.id}>
										<SessionLeft>
											<SessionNameRow>
												<SessionName>{displayName}</SessionName>
												<TypeLabel>{categoryLabel(session)}</TypeLabel>
											</SessionNameRow>
											<MetaRow>
												{meta.map(function (part, i) {
													var els = []
													if (i > 0) els.push(<MetaDivider key={'d-' + i} />)
													if (part.type === 'active') {
														els.push(<ActiveIndicator key={'m-' + i}>Active now</ActiveIndicator>)
													} else {
														els.push(<MetaChip key={'m-' + i}>{part.value}</MetaChip>)
													}
													return els
												})}
											</MetaRow>
										</SessionLeft>
										<SessionRight>
											<Button
												onClick={function () { revokeSession(session.id) }}
												disabled={revokingId === session.id}
												variant='secondary'
												loading={revokingId === session.id}
												style={{ padding: '4px 12px', fontSize: '12px' }}
											>Revoke</Button>
										</SessionRight>
									</SessionItem>
								)
							})}
						</SessionCard>
					</Col>
				</Row>
			</StyledContainer>
		</ScreenWrapper>
	)
}
